import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

const TOUR_STORAGE_KEY = 'app-tour-completed';

export function useAppTour() {
  const { user } = useAuth();
  const [shouldShowTour, setShouldShowTour] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    async function checkTourStatus() {
      if (!user) {
        setIsChecking(false);
        return;
      }

      // Check if tour was already completed
      const tourCompleted = localStorage.getItem(`${TOUR_STORAGE_KEY}-${user.id}`);
      if (tourCompleted) {
        setShouldShowTour(false);
        setIsChecking(false);
        return;
      }

      // Check if user has a profile with username (completed onboarding)
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('username, created_at')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking profile for tour:', error);
        setIsChecking(false);
        return;
      }

      // Only show tour if user has completed profile setup (has username)
      // and account is relatively new (created within last 7 days)
      if (profile?.username) {
        const createdAt = new Date(profile.created_at);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        
        // Show tour for new users who haven't seen it
        const isNewUser = createdAt > sevenDaysAgo;
        setShouldShowTour(isNewUser);
      } else {
        setShouldShowTour(false);
      }
      
      setIsChecking(false);
    }

    checkTourStatus();
  }, [user]);

  const completeTour = () => {
    if (user) {
      localStorage.setItem(`${TOUR_STORAGE_KEY}-${user.id}`, 'true');
      setShouldShowTour(false);
    }
  };

  const resetTour = () => {
    if (user) {
      localStorage.removeItem(`${TOUR_STORAGE_KEY}-${user.id}`);
      setShouldShowTour(true);
    }
  };

  return {
    shouldShowTour,
    isChecking,
    completeTour,
    resetTour,
  };
}
